/**
 * EndOfDaySummaryTab — End-of-day yield summary tab for the SupervisorOverrideModal.
 * Shows session metrics, weight breakdown, yield comparison, and sign-off action.
 * Extracted from SupervisorOverrideModal.tsx for maintainability.
 */
import React from 'react';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';

interface EndOfDaySummaryTabProps {
  elapsedTimeFormatted: string;
  boxCount: number;
  pace: number;
  progressPct: number;
  inputWeight: number;
  outputWeight: number;
  wasteWeight: number;
  returnsWeight: number;
  isWeightBalanced: boolean;
  expectedYield: number;
  yieldPct: number;
  variance: number;
  signedOff: boolean;
  signingOff: boolean;
  supervisorName: string;
  onSignOff: () => void;
}

const EndOfDaySummaryTab: React.FC<EndOfDaySummaryTabProps> = ({
  elapsedTimeFormatted,
  boxCount,
  pace,
  progressPct,
  inputWeight,
  outputWeight,
  wasteWeight,
  returnsWeight,
  isWeightBalanced,
  expectedYield,
  yieldPct,
  variance,
  signedOff,
  signingOff,
  supervisorName,
  onSignOff,
}) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-3">
      {/* Row 1: Key metrics - 3 columns */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.elapsedTime') || 'Elapsed'}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{elapsedTimeFormatted}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.boxCount')}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{boxCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.pace') || 'Pace'}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{pace.toFixed(2)}/hr</p>
        </div>
      </div>

      {/* Row 2: Progress bar - full width */}
      <div className="bg-white dark:bg-gray-800 p-3 rounded-xl">
        <div className="flex justify-between items-center mb-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.progress') || 'Progress'}</p>
          <p className={`text-sm font-bold ${progressPct >= 100 ? 'text-green-600' : 'text-gray-900 dark:text-white'}`}>
            {progressPct.toFixed(2)}%
          </p>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${progressPct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${Math.min(progressPct, 100)}%` }}
          />
        </div>
      </div>

      {/* Row 3: Weight breakdown - Input = Waste + Returns + Output */}
      <div className={`p-3 rounded-xl border ${isWeightBalanced
        ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
        : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'}`}>
        <div className="flex items-center justify-between text-sm gap-1">
          <div className="text-center min-w-0 flex-1">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{t('supervisorOverride.inputWeight')}</p>
            <p className="font-bold text-gray-900 dark:text-white text-sm">{inputWeight.toFixed(2)}</p>
          </div>
          <span className={`text-xs font-bold ${isWeightBalanced ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>=</span>
          <div className="text-center min-w-0 flex-1">
            <p className="text-[10px] text-red-500 dark:text-red-400 truncate">{t('mobile.waste') || 'Waste'}</p>
            <p className="font-bold text-sm text-red-600 dark:text-red-400">{wasteWeight.toFixed(2)}</p>
          </div>
          <span className="text-gray-400 text-xs">+</span>
          <div className="text-center min-w-0 flex-1">
            <p className="text-[10px] text-blue-500 dark:text-blue-400 truncate">{t('mobile.returns') || 'Returns'}</p>
            <p className="font-bold text-sm text-blue-600 dark:text-blue-400">{returnsWeight.toFixed(2)}</p>
          </div>
          <span className="text-gray-400 text-xs">+</span>
          <div className="text-center min-w-0 flex-1">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{t('supervisorOverride.outputWeight')}</p>
            <p className={`font-bold text-sm ${isWeightBalanced ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>{outputWeight.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Row 4: Yield metrics - 2 columns */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.expectedYield')}</p>
          <p className="text-base font-bold text-gray-900 dark:text-white">{formatYield(expectedYield)}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('supervisorOverride.actualYield')}</p>
          <p className={`text-base font-bold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatYield(yieldPct)}
          </p>
        </div>
      </div>

      {signedOff ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="font-semibold text-green-800 dark:text-green-200">
              {t('supervisorOverride.signedOff')}
            </span>
          </div>
          <span className="text-sm text-green-600 dark:text-green-400">{supervisorName}</span>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
              {t('supervisorOverride.pendingSignOff') || 'Pending'}
            </span>
          </div>
          <button
            onClick={onSignOff}
            disabled={signingOff}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {signingOff ? '...' : t('supervisorOverride.signOff')}
          </button>
        </div>
      )}
    </div>
  );
};

export default EndOfDaySummaryTab;
